// client/src/components/reviews/ReviewCard.js
import React, { useState } from 'react';
import {
  Box, Avatar, Typography, Chip, IconButton, Tooltip,
  Collapse, Button
} from '@mui/material';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import StarIcon from '@mui/icons-material/Star';
import VerifiedIcon from '@mui/icons-material/Verified';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import axiosInstance from '../../axiosInstance';
import ReputationBadge from '../reputation/ReputationBadge';
import CommentThread from '../social/CommentThread';
import FollowButton from '../social/FollowButton';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';

export default function ReviewCard({ review, currentUserId }) {
  const { isFeatureEnabled } = useFeatureFlags();
  const [liked, setLiked] = useState(
    review.likes?.some(l => String(l.userId) === String(currentUserId)) || false
  );
  const [likeCount, setLikeCount] = useState(review.likeCount || 0);
  const [disliked, setDisliked] = useState(
    review.dislikes?.some(d => String(d.userId) === String(currentUserId)) || false
  );
  const [dislikeCount, setDislikeCount] = useState(review.dislikeCount || 0);
  const [spoilerRevealed, setSpoilerRevealed] = useState(false);
  const [showComments, setShowComments] = useState(false);

  const user = review.userId;
  const isOwn = String(user?._id) === String(currentUserId);

  const handleLike = async () => {
    try {
      if (liked) {
        await axiosInstance.delete(`/reviews/${review._id}/like`);
        setLiked(false);
        setLikeCount(c => c - 1);
      } else {
        await axiosInstance.post(`/reviews/${review._id}/like`);
        setLiked(true);
        setLikeCount(c => c + 1);
        if (disliked) { setDisliked(false); setDislikeCount(c => c - 1); }
      }
    } catch (err) {
      console.error('Review like error:', err);
    }
  };

  const handleDislike = async () => {
    try {
      if (disliked) {
        await axiosInstance.delete(`/reviews/${review._id}/dislike`);
        setDisliked(false);
        setDislikeCount(c => c - 1);
      } else {
        const res = await axiosInstance.post(`/reviews/${review._id}/dislike`);
        setDisliked(true);
        setDislikeCount(c => c + 1);
        if (liked) { setLiked(false); setLikeCount(res.data.likeCount ?? likeCount - 1); }
      }
    } catch (err) {
      console.error('Review dislike error:', err);
    }
  };

  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2, mb: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1 }}>
        <Avatar src={user?.profileImage} sx={{ width: 36, height: 36 }} />
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
            <Typography variant="body2" fontWeight={600}>{user?.username}</Typography>
            <ReputationBadge trustLevel={user?.trustLevel} />
            {isFeatureEnabled('enableFollowSystem') && (
              <FollowButton targetUserId={user?._id} currentUserId={currentUserId} size="small" />
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
            {review.itemWasSold
              ? <Chip icon={<VerifiedIcon />} label="Verified previous owner" size="small" color="info" />
              : <Chip icon={<VerifiedIcon />} label="Verified owner" size="small" color="success" />}
            <Typography variant="caption" color="text.secondary">
              {new Date(review.createdAt).toLocaleDateString()}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Title */}
      <Typography variant="subtitle2" fontWeight={700} gutterBottom>{review.title}</Typography>

      {/* Body */}
      {review.containsSpoilers && !spoilerRevealed ? (
        <Box
          sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 1, cursor: 'pointer', userSelect: 'none' }}
          onClick={() => setSpoilerRevealed(true)}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <VisibilityOffIcon fontSize="small" />
            <Typography variant="body2" color="text.secondary">
              Spoiler warning — click to reveal
            </Typography>
          </Box>
        </Box>
      ) : (
        <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{review.body}</Typography>
      )}

      {isFeatureEnabled('enableRicherReactions') && review.moodTags?.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
          {review.moodTags.map(tag => (
            <Chip key={tag} label={tag} size="small" variant="outlined" />
          ))}
        </Box>
      )}

      {/* Footer */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
        {!isOwn && (
          <>
            <IconButton size="small" onClick={handleLike}>
              {liked ? <ThumbUpIcon fontSize="small" color="primary" /> : <ThumbUpOutlinedIcon fontSize="small" />}
            </IconButton>
            {likeCount > 0 && <Typography variant="caption">{likeCount}</Typography>}
            <IconButton size="small" onClick={handleDislike}>
              {disliked ? <ThumbDownIcon fontSize="small" color="error" /> : <ThumbDownOutlinedIcon fontSize="small" />}
            </IconButton>
            {dislikeCount > 0 && <Typography variant="caption" color="text.secondary">{dislikeCount}</Typography>}
          </>
        )}
        {isFeatureEnabled('enableComments') && (
          <Button size="small" onClick={() => setShowComments(s => !s)} endIcon={<ExpandMoreIcon />}>
            Comments
          </Button>
        )}
      </Box>

      {/* Comments */}
      {isFeatureEnabled('enableComments') && showComments && (
        <CommentThread targetType="review" targetId={review._id} currentUserId={currentUserId} />
      )}
    </Box>
  );
}
